import React, { useContext, useEffect, useState } from "react";
import Packky from "../../assets/Packky.png";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Menu,
  X,
  Clock3,
  LogOut,
  User,
  Info,
  Newspaper,
  Phone,
  Building2,
  Truck,
  Home,
  MapPin,
  FileText,
  Pencil,
  Loader2,
  CalendarDays,
  Mail,
} from "lucide-react";

import { GlobalContext } from "../../Context/GlobalContext";
import useCustomerDetails from "../Services/Customer/useCustomerDetails";

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { user, userCode, clearUser, saveUser } = useContext(GlobalContext);
  const isLoggedIn = Boolean(
    user || userCode || sessionStorage.getItem("user"),
  );

  const {
    getCustomerProfileDetailsApi,
    customerProfileLoading,
    editCustomerDetailsApi,
    editCustomerDetailsLoading,
  } = useCustomerDetails();

  const [openSidebar, setOpenSidebar] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [customerProfile, setCustomerProfile] = useState(null);
  const [showEditProfileModal, setShowEditProfileModal] = useState(false);

  const [editProfileForm, setEditProfileForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    dateOfBirth: "",
  });

  const cleanMobile = (value) => {
    if (!value) return "";

    return String(value).replace(/\D/g, "");
  };

  const extractProfileData = (apiResponse) => {
    const data =
      apiResponse?.response ||
      apiResponse?.data ||
      apiResponse?.customer ||
      apiResponse?.result ||
      apiResponse;

    if (Array.isArray(data)) {
      return data[0] || null;
    }

    if (Array.isArray(data?.data)) {
      return data.data[0] || null;
    }

    if (Array.isArray(data?.response)) {
      return data.response[0] || null;
    }

    if (Array.isArray(data?.customer)) {
      return data.customer[0] || null;
    }

    if (Array.isArray(data?.result)) {
      return data.result[0] || null;
    }

    return data || null;
  };

  const profileName =
    customerProfile?.fullName ||
    customerProfile?.name ||
    customerProfile?.customerName ||
    customerProfile?.username ||
    `${customerProfile?.firstName || customerProfile?.first_name || ""} ${
      customerProfile?.lastName || customerProfile?.last_name || ""
    }`.trim() ||
    user?.fullName ||
    user?.name ||
    user?.username ||
    `${user?.firstName || user?.first_name || ""} ${
      user?.lastName || user?.last_name || ""
    }`.trim() ||
    "Guest User";

  const profileMobile = cleanMobile(
    customerProfile?.mobile ||
      customerProfile?.phone ||
      customerProfile?.mobileNumber ||
      customerProfile?.customerMobile ||
      customerProfile?.phoneNumber ||
      customerProfile?.contact ||
      customerProfile?.contactNo ||
      customerProfile?.contact_no ||
      customerProfile?.mobile_no ||
      customerProfile?.mobileNo ||
      customerProfile?.phone_no ||
      customerProfile?.phoneNo ||
      customerProfile?.customer_phone ||
      customerProfile?.customerPhone ||
      customerProfile?.custMobile ||
      customerProfile?.cust_mobile ||
      customerProfile?.mobile_number ||
      customerProfile?.phone_number ||
      user?.mobile ||
      user?.phone ||
      user?.mobileNumber ||
      user?.phoneNumber ||
      user?.customerMobile ||
      user?.contact ||
      user?.mobile_no ||
      user?.phone_no ||
      user?.mobile_number ||
      user?.phone_number ||
      "",
  );

  const formatDateForInput = (dateValue) => {
    if (!dateValue) return "";

    const date = new Date(dateValue);

    if (Number.isNaN(date.getTime())) return "";

    return date.toISOString().split("T")[0];
  };

  const openEditProfileModal = () => {
    setEditProfileForm({
      firstName:
        customerProfile?.firstName ||
        customerProfile?.first_name ||
        user?.firstName ||
        user?.first_name ||
        "",
      lastName:
        customerProfile?.lastName ||
        customerProfile?.last_name ||
        user?.lastName ||
        user?.last_name ||
        "",
      email:
        customerProfile?.email ||
        customerProfile?.emailId ||
        customerProfile?.email_id ||
        user?.email ||
        user?.emailId ||
        "",
      dateOfBirth: formatDateForInput(
        customerProfile?.dateOfBirth ||
          customerProfile?.date_of_birth ||
          customerProfile?.dob ||
          user?.dateOfBirth ||
          user?.date_of_birth ||
          user?.dob,
      ),
    });

    setShowEditProfileModal(true);
  };

  const closeEditProfileModal = () => {
    setShowEditProfileModal(false);
  };

  const handleEditProfileChange = (e) => {
    const { name, value } = e.target;

    setEditProfileForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const refreshCustomerProfile = async () => {
    if (!userCode) return;

    const result = await getCustomerProfileDetailsApi({
      userId: userCode,
    });

    const profileData = extractProfileData(result);

    if (profileData) {
      setCustomerProfile(profileData);
    }
  };

  const handleSaveProfileDetails = async () => {
    console.log("SAVE CLICKED");

    if (!editProfileForm.firstName.trim()) {
      return;
    }

    if (!editProfileForm.lastName.trim()) {
      return;
    }

    const result = await editCustomerDetailsApi({
      userId: userCode,
      firstName: editProfileForm.firstName.trim(),
      lastName: editProfileForm.lastName.trim(),
      email: editProfileForm.email.trim(),
      dateOfBirth: editProfileForm.dateOfBirth,
    });

    if (result?.responseCode === "00") {
      await refreshCustomerProfile();

      const updatedUser = {
        ...(user || {}),
        firstName: editProfileForm.firstName.trim(),
        lastName: editProfileForm.lastName.trim(),
        email: editProfileForm.email.trim(),
        dateOfBirth: editProfileForm.dateOfBirth,
      };

      sessionStorage.setItem("user", JSON.stringify(updatedUser));

      setShowEditProfileModal(false);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const fetchCustomerProfile = async () => {
      if (!userCode) return;

      const result = await getCustomerProfileDetailsApi({
        userId: userCode,
      });

      const profileData = extractProfileData(result);

      if (profileData) {
        setCustomerProfile(profileData);
      }
    };

    fetchCustomerProfile();
  }, [userCode]);

  const mainLinks = [
    {
      label: "About Us",
      path: "/about-us",
      icon: Info,
    },
    {
      label: "Blog",
      path: "/blog-section",
      icon: Newspaper,
    },
    {
      label: "Contact Us",
      path: "/contact-us",
      icon: Phone,
    },
    {
      label: "Enterprise",
      path: "/enterprise",
      icon: Building2,
    },
    {
      label: "Driver",
      path: "/driver-partner",
      icon: Truck,
    },
  ];

  const userLinks = [
    {
      label: "My Rides",
      path: "/my-rides",
      icon: Clock3,
      disabled: true,
    },
    {
      label: "Address",
      path: "/address",
      icon: MapPin,
    },
    {
      label: "Terms & Conditions",
      path: "/term-condition",
      icon: FileText,
    },
  ];

  const isActivePath = (path) => {
    if (path === "/") {
      return location.pathname === "/";
    }

    return (
      location.pathname === path || location.pathname.startsWith(`${path}/`)
    );
  };

  const handleNavigate = (path) => {
    navigate(path);
    setOpenSidebar(false);
  };

  const handleLogout = () => {
    sessionStorage.clear();

    if (clearUser) {
      clearUser();
    }

    setOpenSidebar(false);
    navigate("/");
  };

  const SidebarButton = ({ item }) => {
    const Icon = item.icon;
    const active = isActivePath(item.path);

    return (
      <button
        onClick={() => !item.disabled && handleNavigate(item.path)}
        disabled={item.disabled}
        className={`flex w-full items-center gap-4 rounded-2xl px-3 py-3 text-left text-sm font-medium transition ${
          item.disabled
            ? "cursor-not-allowed opacity-50 bg-gray-100 text-gray-400"
            : active
              ? "bg-orange-50 text-orange-700"
              : "text-slate-700 hover:bg-orange-100 hover:text-orange-600"
        }`}
      >
        <span
          className={`flex h-9 w-9 items-center justify-center rounded-xl ${
            item.disabled
              ? "bg-gray-200 text-gray-400"
              : active
                ? "bg-orange-600 text-white"
                : "bg-slate-100 text-slate-700"
          }`}
        >
          <Icon size={18} />
        </span>

        <span className="flex-1">{item.label}</span>

        {active && !item.disabled && (
          <span className="h-2 w-2 rounded-full bg-orange-600" />
        )}
      </button>
    );
  };

  return (
    <>
      <nav
        className={`fixed left-1/2 z-[1000] -translate-x-1/2 border border-white bg-white px-3 shadow-lg will-change-transform transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${
          isScrolled
            ? "top-0 h-[74px] w-full max-w-none rounded-none"
            : "top-3 h-[70px] w-[94vw] max-w-[1180px] rounded-full"
        }`}
      >
        <div
          className={`mx-auto flex h-full items-center justify-between ${
            isScrolled ? "max-w-[1180px]" : "max-w-full"
          }`}
        >
          <div
            className="flex cursor-pointer items-center justify-center pl-2 sm:pl-4"
            onClick={() => navigate("/")}
          >
            <img src={Packky} alt="Logo" className="h-11 w-auto sm:h-12" />
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/bookings")}
              className="hidden rounded-full bg-orange-500 px-5 py-2.5 text-sm font-semibold text-white shadow-md transition hover:-translate-y-[1px] md:block"
            >
              Book Now
            </button>

            <button
              onClick={() => setOpenSidebar(true)}
              className="flex h-11 w-11 items-center justify-center rounded-full bg-slate-100 text-slate-800 shadow-sm transition hover:bg-slate-200"
            >
              <Menu size={22} />
            </button>
          </div>
        </div>
      </nav>

      {openSidebar && (
        <div className="fixed inset-0 z-[9999] bg-black/45">
          <div
            className="absolute inset-0"
            onClick={() => setOpenSidebar(false)}
          />

          <div className="relative ml-auto flex h-full w-[88%] max-w-[360px] flex-col overflow-hidden rounded-l-[28px] bg-white shadow-2xl">
            <div className="border-b bg-gradient-to-br from-orange-400 via-orange-500 to-orange-600 px-5 pb-6 pt-5 text-white">
              <div className="mb-6 flex items-center justify-between">
                <img
                  src={Packky}
                  alt="Logo"
                  className="h-11 w-auto bg-white rounded-full p-1 px-3"
                />

                <button
                  onClick={() => setOpenSidebar(false)}
                  className="rounded-full bg-white/10 p-2 text-white transition hover:bg-white/20"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="flex items-start gap-3">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white/20 ring-2 ring-white/30">
                  <User size={28} className="text-white" />
                </div>

                <div className="min-w-0 flex-1">
                  {customerProfileLoading ? (
                    <>
                      <div className="mb-2 h-4 w-32 animate-pulse rounded bg-white/30" />
                      <div className="h-3 w-24 animate-pulse rounded bg-white/20" />
                    </>
                  ) : (
                    <>
                      <h3 className="truncate text-base font-semibold text-white">
                        {profileName}
                      </h3>

                      <p className="mt-0.5 truncate text-sm text-white/80">
                        {profileMobile
                          ? `+91 ${profileMobile}`
                          : "Mobile not available"}
                      </p>
                    </>
                  )}
                </div>

                {user && (
                  <button
                    onClick={openEditProfileModal}
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/15 text-white transition hover:bg-white hover:text-orange-600"
                  >
                    <Pencil size={16} />
                  </button>
                )}
              </div>
            </div>

            <div className="px-4 pt-4 md:hidden">
              <button
                onClick={() => handleNavigate("/bookings")}
                className="w-full rounded-2xl bg-orange-600 px-4 py-3 text-sm font-semibold text-white shadow-md transition hover:bg-orange-700"
              >
                Book Now
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-5 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
              <div className="mb-3 px-1 text-xs font-semibold uppercase tracking-wider text-gray-400">
                Main Menu
              </div>

              <div className="space-y-1">
                <SidebarButton
                  item={{
                    label: "Home",
                    path: "/",
                    icon: Home,
                  }}
                />

                {mainLinks.map((item) => (
                  <SidebarButton key={item.label} item={item} />
                ))}
              </div>

              <div className="mb-3 mt-6 px-1 text-xs font-semibold uppercase tracking-wider text-gray-400">
                Account
              </div>

              <div className="space-y-1">
                {userLinks.map((item) => (
                  <SidebarButton key={item.label} item={item} />
                ))}
              </div>
            </div>

            {isLoggedIn && (
              <div className="border-t bg-gray-50 p-4">
                <button
                  onClick={handleLogout}
                  className="flex w-full items-center justify-center gap-3 rounded-2xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-600 transition hover:bg-red-100"
                >
                  <LogOut size={18} />
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* profileEdit details model  */}

      {showEditProfileModal && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-[520px] overflow-hidden rounded-[26px] bg-white shadow-2xl">
            <div className="relative overflow-hidden bg-gradient-to-br from-orange-400 via-orange-500 to-orange-600 px-5 pb-6 pt-5 text-white">
              <div className="absolute -right-16 -top-16 h-40 w-40 rounded-full bg-white/20 blur-2xl" />
              <div className="absolute -bottom-20 -left-10 h-44 w-44 rounded-full bg-white/10 blur-2xl" />

              <div className="relative flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/20 ring-1 ring-white/30">
                    <User size={24} />
                  </div>

                  <div>
                    <h3 className="text-lg font-bold">Edit Profile</h3>
                    <p className="mt-0.5 text-sm text-white/80">
                      Update or complete your personal details.
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={closeEditProfileModal}
                  disabled={editCustomerDetailsLoading}
                  className="rounded-full bg-white/10 p-2 text-white transition hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className="space-y-4 p-5">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-bold text-slate-700">
                    First Name
                  </label>

                  <div className="flex h-[48px] items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 transition focus-within:border-orange-400 focus-within:bg-white focus-within:ring-4 focus-within:ring-orange-100">
                    <User size={16} className="text-slate-400" />
                    <input
                      type="text"
                      name="firstName"
                      value={editProfileForm.firstName}
                      onChange={handleEditProfileChange}
                      placeholder="Enter first name"
                      className="w-full bg-transparent text-sm font-semibold text-slate-700 outline-none placeholder:font-normal placeholder:text-slate-400"
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-bold text-slate-700">
                    Last Name
                  </label>

                  <div className="flex h-[48px] items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 transition focus-within:border-orange-400 focus-within:bg-white focus-within:ring-4 focus-within:ring-orange-100">
                    <User size={16} className="text-slate-400" />
                    <input
                      type="text"
                      name="lastName"
                      value={editProfileForm.lastName}
                      onChange={handleEditProfileChange}
                      placeholder="Enter last name"
                      className="w-full bg-transparent text-sm font-semibold text-slate-700 outline-none placeholder:font-normal placeholder:text-slate-400"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-bold text-slate-700">
                  Email Address
                </label>

                <div className="flex h-[48px] items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 transition focus-within:border-orange-400 focus-within:bg-white focus-within:ring-4 focus-within:ring-orange-100">
                  <Mail size={16} className="text-slate-400" />
                  <input
                    type="email"
                    name="email"
                    value={editProfileForm.email}
                    onChange={handleEditProfileChange}
                    placeholder="Enter email address"
                    className="w-full bg-transparent text-sm font-semibold text-slate-700 outline-none placeholder:font-normal placeholder:text-slate-400"
                  />
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-bold text-slate-700">
                  Date of Birth
                </label>

                <div className="flex h-[48px] items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 transition focus-within:border-orange-400 focus-within:bg-white focus-within:ring-4 focus-within:ring-orange-100">
                  <CalendarDays size={16} className="text-slate-400" />
                  <input
                    type="date"
                    name="dateOfBirth"
                    value={editProfileForm.dateOfBirth}
                    onChange={handleEditProfileChange}
                    className="w-full bg-transparent text-sm font-semibold text-slate-700 outline-none"
                  />
                </div>
              </div>

              {profileMobile && (
                <div className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3">
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
                    Registered Mobile
                  </p>
                  <p className="mt-1 text-sm font-bold text-slate-700">
                    +91 {profileMobile}
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 border-t border-slate-100 bg-slate-50 px-5 py-4">
              <button
                type="button"
                onClick={closeEditProfileModal}
                disabled={editCustomerDetailsLoading}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleSaveProfileDetails}
                disabled={editCustomerDetailsLoading}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-orange-600 px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-orange-200 transition hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {editCustomerDetailsLoading ? (
                  <>
                    <Loader2 size={17} className="animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Pencil size={16} />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
